import { CombinedGraphQLErrors } from '@apollo/client';
import { CreditPackPurchaseRefusal } from '@insio/shared-types';
import { GraphQLErrorCode } from '@insio/validation';

/** How a failed `purchaseCreditPack` reads to the customer. */
export const CreditPackPurchaseErrorKind = {
  REFUSED: 'REFUSED',
  NOT_OFFERED: 'NOT_OFFERED',
  /** Stripe unavailable (503) or anything unexpected. */
  GENERIC: 'GENERIC'
} as const;

export type CreditPackPurchaseErrorKind =
  (typeof CreditPackPurchaseErrorKind)[keyof typeof CreditPackPurchaseErrorKind];

export type CreditPackPurchaseError =
  | { kind: typeof CreditPackPurchaseErrorKind.REFUSED; refusal: CreditPackPurchaseRefusal }
  | { kind: typeof CreditPackPurchaseErrorKind.NOT_OFFERED }
  | { kind: typeof CreditPackPurchaseErrorKind.GENERIC };

const REFUSALS: ReadonlySet<string> = new Set(Object.values(CreditPackPurchaseRefusal));

function isRefusal(value: unknown): value is CreditPackPurchaseRefusal {
  return typeof value === 'string' && REFUSALS.has(value);
}

/** Sorts a `purchaseCreditPack` failure by its code and, for a refusal, its `extensions.reason`. */
export function creditPackPurchaseError(error: unknown): CreditPackPurchaseError {
  if (CombinedGraphQLErrors.is(error)) {
    for (const { extensions } of error.errors) {
      const code = extensions?.['code'];
      if (code === GraphQLErrorCode.CREDIT_PACK_NOT_OFFERED) {
        return { kind: CreditPackPurchaseErrorKind.NOT_OFFERED };
      }
      const reason = extensions?.['reason'];
      if (code === GraphQLErrorCode.CREDIT_PACK_PURCHASE_REFUSED && isRefusal(reason)) {
        return { kind: CreditPackPurchaseErrorKind.REFUSED, refusal: reason };
      }
    }
  }
  return { kind: CreditPackPurchaseErrorKind.GENERIC };
}
